import React from "react";
import finishedwax from "../static/lost-wax-casting/finishedwax.png";
import finishedwaxwebp from "../static/lost-wax-casting/finishedwax.webp";
import sprue from "../static/lost-wax-casting/sprue.png";
import spruewebp from "../static/lost-wax-casting/sprue.webp";
import plainwax from "../static/lost-wax-casting/plainwax.png";
import benchwebp from "../static/lost-wax-casting/plainwax.webp";
import { JoinNewsLetterButton } from "../components/Common/NewsLetterPlug";
import Layout from "../components/layout";
import { PageTitle } from "../components/Common/PageTitle";
import { Photo } from "../components/Photo";
import { LanguageToggle } from "../components/Common/LanguageToggle";

// Text content in both languages - now HTML-aware
const content = {
  en: {
    title: "Wax carving and the lost wax casting process",
    subtitle: "",
    metaDescription:
      "Hi I'm Sandy! I decided to teach wax carving to make wax carving available to you so you can go ahead and fall in love with it yourself. You will learn through video tutorials, written handouts and have the ability to ask questions.",
    intro1:
      "Wax carving is a very old and traditional jewellery making technique. That's right. Chances are the goldsmith you buy your jewellery from uses this technique from time to time!",
    intro2:
      "And I can learn how to make jewellery through wax carving? Absolutely! You can learn this fun jewellery making technique as well!",
    waxCarvingTitle: "Wax carving",
    waxCarving1:
      "You start with a piece of unassuming jewellers wax, your imagination and creativity.",
    plainWaxAlt:
      "piece of flat blue wax ring tube and piece of square blue wax",
    waxCarving2:
      "You design and sketch the piece of jewellery you want to make. Wax carving goes through an extended ugly stage. Keeping the end goal in mind is important to refrain from throwing your piece across the room because it doesn't look like you want it to yet.",
    waxCarving3:
      "You then saw, file, carve, drill, and melt the piece of wax into an amazing piece of jewellery! Wax carving is especially well suited to 3d shapes, so think big and let your imagination run wild for your design!",
    finishedWaxAlt:
      "blue wax ring with a carved rocky texture on the top, blue wax flower pendant on a silver chain",
    waxCarving4:
      "Unfortunately this wax piece is too soft to wear. Not to worry. Through the magic of lost wax casting your wax piece gets turned into a silver (or gold) piece that you can wear!",
    lostWaxCastingTitle: "Lost wax casting",
    lostWaxCasting1:
      "When your wax piece is done I send it to a casting company. They make a mould of your piece and melt your wax version out of the mould. It's the lost part of lost wax casting! Your wax piece gets lost (melted down) in the process. Now molten silver can be poured into the mould and you have an exact replica of the ring/pendant you made in wax!",
    lostWaxCasting2:
      "After casting your piece isn't ready for immediate wear just yet. The channel that the molten silver flowed through is still attached, and it can be a bit rough around the edges. The casting company sends your piece back to me and I will clean it up beautifully and polish it for you. Or you can do this part yourself as well!",
    lostWaxCasting3:
      "That's all there is to wax carving and the lost wax casting process. Ready to give it a try? Look through the available <a href='/in-person-classes' className='text-blue-600 hover:text-blue-800 underline'>in-person classes</a> to find the class you want to take!",
  },
  nl: {
    title: "Wax carving en het verloren was proces",
    subtitle: "",
    metaDescription:
      "Hoi, ik ben Sandy! Ik besloot om wax carving te onderwijzen om wax carving beschikbaar te maken voor jou, zodat je er zelf verliefd op kunt worden. Je leert door middel van video tutorials, geschreven handleidingen en je kunt vragen stellen.",
    intro1:
      "Wax carving is een heel oude en traditionele manier van sieraden maken. De kans is groot dat de goudsmid waar jij je sieraden koopt deze techniek ook gebruikt.",
    intro2:
      "En ik kan leren hoe je sieraden maakt door wax carving? Ja! Jij kan deze leuke manier van sieraden maken ook leren.",
    waxCarvingTitle: "Wax carving",
    waxCarving1: "Je begint met een stukje was en je creativiteit.",
    plainWaxAlt:
      "stuk platte blauwe wax ring buis en stuk vierkante blauwe wax",
    waxCarving2:
      "Je ontwerpt en schets het sieraad dat je wilt maken. Wax carving gaat door een lange ugly stage. Je ontwerp in gedachte houden is belangrijk zodat je je stukje was niet door de kamer gooit wanneer het er nog niet uitziet zoals je wilt.",
    waxCarving3:
      "Dan vijl, zaag, snij, boor, en smelt je je stukje was om tot een fantastisch sieraad. Wax carving werkt heel goed met 3d ontwerpen. Denk groots en laat je fantasie op hol slaan voor je ontwerp.",
    finishedWaxAlt:
      "blauwe wax ring met een gebeeldhouwde rotsachtige textuur bovenop, blauwe wax bloem hanger aan een zilveren ketting",
    waxCarving4:
      "Helaas is je wassen sieraad te zacht om zo te dragen. Maar geen probleem! Door de magie van het verloren was proces wordt je wassen sieraad omgetoverd tot een zilveren (of gouden) model dat je wel kan dragen.",
    lostWaxCastingTitle: "Verloren was proces",
    lostWaxCasting1:
      "Als je sieraad af is stuur ik deze naar een gieterij. Daar maken ze een mal van je sieraad en smelten ze de wassen versie uit de mal. Dat is het verloren gedeelte! Je wassen siaraad gaat verloren (wordt gesmolten) in het proces. Nu kan er vloeibaar zilver in de mal worden gegoten en krijg je een exacte replica van je wassen sieraad!",
    sprueAlt:
      "Zilveren hanger in een oneven hart vorm met gietkanaal links boven aan een zilveren ketting",
    lostWaxCasting2:
      "Na het gieten is je sieraad niet meteen klaar om gedragen te worden. Het gietkanaal dat gebruikt wordt voor het vloeibare zilver zit er nog aan, en het heeft wat ruwe kantjes. De gieterij stuurt jouw sieraad terug naar mij en ik maak deze mooi schoon en polijst hem voor je. Of je kan dit gedeelte ook zelf doen!",
    lostWaxCasting3:
      "Dat is wax carving en het verloren was proces. Klaar om het zelf te proberen? Bekijk de beschikbare <a href='/in-person-classes' className='text-blue-600 hover:text-blue-800 underline'>lessen</a> en boek de les die bij je past!",
  },
};

// Component to render HTML content safely
const HTMLContent = ({ content, className = "" }) => {
  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

const About = () => {
  const [lang, setLang] = React.useState("nl");
  const t = content[lang];
  
  return (
    <Layout
      title="Wax Carvers - About Wax Carvers"
      description={t.metaDescription}
    >
      <div className="relative pb-6 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0">
          <div className="bg-white h-1/3 sm:h-2/3"></div>
        </div>
        <div className="relative">
          <LanguageToggle lang={lang} setLang={setLang} />
          <div className="text-lg max-w-prose mx-auto">
            <PageTitle
              title={t.title}
              subtitle={t.subtitle}
            />
            <div className="mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto">
              <p>
                <HTMLContent content={t.intro1} />
              </p>
              <p>
                <HTMLContent content={t.intro2} />
              </p>
            </div>

            <h2 className="mb-6 mt-12 block text-3xl text-center leading-6 tracking-tight text-blue-700 sm:text-4xl">
              {t.waxCarvingTitle}
            </h2>
            <div className="mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto">
              <p>
                <HTMLContent content={t.waxCarving1} />
              </p>
              <Photo
                collage={plainwax}
                collagewp={benchwebp}
                altText={t.plainWaxAlt}
                small
              />
            </div>

            <div className="mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto">
              <p>
                <HTMLContent content={t.waxCarving2} />
              </p>
              <p>
                <HTMLContent content={t.waxCarving3} />
              </p>
              <Photo
                collage={finishedwax}
                collagewp={finishedwaxwebp}
                altText={t.finishedWaxAlt}
                small
              />
              <p>
                <HTMLContent content={t.waxCarving4} />
              </p>
            </div>

            <h2 className="mb-6 mt-12 block text-3xl text-center leading-6 tracking-tight text-blue-700 sm:text-4xl">
              {t.lostWaxCastingTitle}
            </h2>
            <div className="mt-6 prose prose-indigo prose-lg text-gray-500 mx-auto">
              <p>
                <HTMLContent content={t.lostWaxCasting1} />
              </p>
              <Photo
                collage={sprue}
                collagewp={spruewebp}
                altText={t.sprueAlt}
                small
              />
              <p>
                <HTMLContent content={t.lostWaxCasting2} />
              </p>
              <p>
                <HTMLContent content={t.lostWaxCasting3} />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <JoinNewsLetterButton />
      </div>
    </Layout>
  );
};

export default About;